import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import {
  type PageImageFailureReason,
  type PageImageResult,
  type PageImageSource,
  type PageImageSurface,
  reportPageImageOutcome,
} from 'features/knowledgeDocumentViewer/telemetry/pageImageOutcome'
import { useCallback, useMemo, useRef } from 'react'

export type PageImageOutcomeReporter = {
  reportDisplayed: (source: PageImageSource) => void
  reportFailed: (
    reason: PageImageFailureReason,
    source?: PageImageSource
  ) => void
  markRetried: () => void
}

type PageImageRun = {
  identity: string
  expectedAt: number
  reported: boolean
  retried: boolean
}

function startRun(identity: string): PageImageRun {
  return { identity, expectedAt: Date.now(), reported: false, retried: false }
}

/**
 * Reports each page image a mounted page slot expected to show, once per
 * mount of a document, page, and surface, timed from the moment the slot
 * asked for it. A slot that unmounts and remounts (virtualized scrolling)
 * reports again. The run restarts during render when the identity changes,
 * because a cached image can report from a ref callback before any effect.
 */
export function usePageImageOutcome({
  pageNumber,
  surface,
  isCurrentPage,
}: {
  pageNumber: number
  surface: PageImageSurface
  isCurrentPage: boolean
}): PageImageOutcomeReporter {
  const { siteID, documentId, imageDocumentId } = useDocumentTabDataContext()
  const identity = `${surface}:${documentId ?? ''}:${pageNumber}`
  const runRef = useRef<PageImageRun | null>(null)
  if (runRef.current?.identity !== identity) {
    runRef.current = startRun(identity)
  }
  const isCurrentPageRef = useRef(isCurrentPage)
  isCurrentPageRef.current = isCurrentPage

  const report = useCallback(
    (result: PageImageResult) => {
      const run = runRef.current
      if (!run || run.reported || !documentId) return
      run.reported = true
      reportPageImageOutcome({
        siteId: siteID,
        documentId: imageDocumentId ?? documentId,
        pageNumber,
        surface,
        isCurrentPage: isCurrentPageRef.current,
        retried: run.retried,
        expectedAt: run.expectedAt,
        result,
      })
    },
    [documentId, imageDocumentId, pageNumber, siteID, surface]
  )

  return useMemo(
    () => ({
      reportDisplayed: (source: PageImageSource) =>
        report({ outcome: 'displayed', source }),
      reportFailed: (
        reason: PageImageFailureReason,
        source?: PageImageSource
      ) => report({ outcome: 'failed', reason, source }),
      markRetried: () => {
        if (runRef.current) runRef.current.retried = true
      },
    }),
    [report]
  )
}
